import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getDatabases } from '../services/api';

const DatabaseContext = createContext();

export const useDatabase = () => {
    const context = useContext(DatabaseContext);
    if (!context) {
        throw new Error('useDatabase must be used within a DatabaseProvider');
    }
    return context;
};

export const DatabaseProvider = ({ children }) => {
    const [databases, setDatabases] = useState([]);
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchDatabases = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getDatabases();
            const dbList = response.data?.data?.databases || [];

            // Map databases with status based on has_analysis flag from backend
            const databasesWithStatus = dbList.map(db => ({
                ...db,
                status: db.has_analysis ? 'analyzed' : 'pending'
            }));

            setDatabases(databasesWithStatus);
        } catch (error) {
            console.error('Failed to fetch databases:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Update status of a specific database (called after analysis completes)
    const updateDatabaseStatus = useCallback(async (dbId) => {
        try {
            // Re-fetch to get updated has_analysis flag
            const response = await getDatabases();
            const dbList = response.data?.data?.databases || [];
            const updatedDb = dbList.find(db => db.id === dbId);
            const status = updatedDb?.has_analysis ? 'analyzed' : 'pending';
            
            setDatabases(prev => prev.map(db =>
                db.id === dbId ? { ...db, status } : db
            ));
            return status;
        } catch (error) {
            console.error(`Failed to update database status for database ${dbId}:`, error);
            return 'pending';
        }
    }, []);

    // Initial fetch on mount (no polling - data is fetched only when needed)
    useEffect(() => {
        const isAuthed =
            localStorage.getItem('isAuthenticated') === 'true' &&
            Boolean(localStorage.getItem('token'));
        if (isAuthed) fetchDatabases();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = {
        databases,
        selectedDatabase,
        setSelectedDatabase,
        loading,
        refreshDatabases: fetchDatabases,
        updateDatabaseStatus,
    };

    return (
        <DatabaseContext.Provider value={value}>
            {children}
        </DatabaseContext.Provider>
    );
};
